import { toast } from "sonner";
import { Sentry } from "@/lib/sentry";

interface ErrorOptions {
  title?: string;
  description?: string;
  fallbackMessage?: string;
}

/**
 * Unified error handler for consistent toast notifications
 * Extracts error message from various error types
 */
export function handleError(
  error: unknown,
  options: ErrorOptions = {}
): void {
  const { 
    title = "Virhe", 
    description,
    fallbackMessage = "Jokin meni pieleen. Yritä uudelleen." 
  } = options;

  let message: string;

  if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === "string") {
    message = error;
  } else if (error && typeof error === "object" && "message" in error) {
    message = String((error as { message: unknown }).message);
  } else {
    message = fallbackMessage;
  }

  toast.error(title, {
    description: description || message,
  });

  // Report to Sentry
  if (error instanceof Error) {
    Sentry.captureException(error, { level: "error" });
  } else if (error) {
    Sentry.captureException(new Error(message), { level: "error" });
  }
}

/**
 * Success toast helper
 */
export function handleSuccess(
  title: string,
  description?: string
): void {
  toast.success(title, {
    description,
  });
}

/**
 * Mutation error handler factory for React Query
 */
export function createMutationErrorHandler(title?: string) {
  return (error: Error) => {
    handleError(error, { title: title || "Virhe tallennuksessa" });
  };
}

/**
 * Mutation success handler factory for React Query
 */
export function createMutationSuccessHandler(
  title: string,
  description?: string,
  onSuccess?: () => void
) {
  return () => {
    handleSuccess(title, description);
    onSuccess?.();
  };
}
